<template>
  <div class="profile-menu dropdown">
    <button
      type="button"
      class="profile-menu__trigger"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      aria-label="منوی حساب کاربری"
    >
      <span class="profile-menu__avatar" :class="{ 'profile-menu__avatar--image': !!avatarSrc }" aria-hidden="true">
        <img v-if="avatarSrc" :src="avatarSrc" alt="" class="profile-menu__avatar-img" />
        <template v-else>
          <i v-if="!initials" class="bi bi-person-fill profile-menu__icon"></i>
          <template v-else>{{ initials }}</template>
        </template>
      </span>
    </button>

    <ul class="dropdown-menu profile-menu__dropdown dropdown-menu-end">
      <li class="profile-menu__header">
        <span class="profile-menu__name">{{ displayName }}</span>
        <span v-if="user?.username" class="profile-menu__phone">@{{ user.username }}</span>
      </li>
      <li><hr class="dropdown-divider" /></li>
      <li>
        <router-link class="dropdown-item profile-menu__item" to="/profile">
          <i class="bi bi-person"></i>
          پروفایل
        </router-link>
      </li>
      <li>
        <router-link class="dropdown-item profile-menu__item" to="/settings">
          <i class="bi bi-gear"></i>
          تنظیمات
        </router-link>
      </li>
      <li><hr class="dropdown-divider" /></li>
      <li>
        <button type="button" class="dropdown-item profile-menu__item profile-menu__item--logout" @click="handleLogout">
          <i class="bi bi-box-arrow-right"></i>
          خروج
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { resolveAvatarSrc } from "@/constants/avatars";

const router = useRouter();
const { user, logout, init } = useAuth();

const displayName = computed(() => {
  const name = user.value?.full_name?.trim();
  if (name) return name;
  if (typeof user.value?.username === "string" && user.value.username) {
    return user.value.username;
  }
  return "حساب کاربری";
});

const avatarSrc = computed(() =>
  resolveAvatarSrc(
    typeof user.value?.avatar_url === "string" ? user.value.avatar_url : null
  )
);

const initials = computed(() => {
  const name = user.value?.full_name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const handle = typeof user.value?.username === "string" ? user.value.username : "";
  if (handle) return handle.slice(0, 2).toUpperCase();
  return "";
});

function handleLogout() {
  logout();
  router.push("/");
}

onMounted(() => {
  init();
});
</script>

<style scoped>
.profile-menu {
  position: relative;
}

.profile-menu__trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 2px solid #ECE4D9;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(76, 55, 42, 0.08);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.profile-menu__trigger:hover,
.profile-menu__trigger:focus-visible {
  border-color: #6D5EF8;
  box-shadow: 0 4px 14px rgba(109, 94, 248, 0.18);
  outline: none;
}

.profile-menu__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #6D5EF8 0%, #865FEA 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  user-select: none;
  overflow: hidden;
}

.profile-menu__avatar--image {
  background: #FFFDF8;
}

.profile-menu__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-menu__icon {
  font-size: 1.25rem;
  line-height: 1;
}

.profile-menu__dropdown {
  min-width: 220px;
  padding: 8px;
  margin-top: 10px !important;
  border: 1px solid #ECE4D9;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(76, 55, 42, 0.12);
  direction: rtl;
  text-align: right;
}

.profile-menu__header {
  padding: 8px 12px 4px;
}

.profile-menu__name {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4C372A;
}

.profile-menu__phone {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #8C8176;
}

.profile-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #4C372A;
}

.profile-menu__item i {
  font-size: 16px;
  color: #6D5EF8;
}

.profile-menu__item:hover,
.profile-menu__item:focus {
  background: #f5f3ff;
  color: #4C372A;
}

.profile-menu__item--logout i {
  color: #dc3545;
}

.profile-menu__item--logout:hover,
.profile-menu__item--logout:focus {
  background: #fff5f5;
  color: #dc3545;
}
</style>
